"use client";

import { useEffect, useRef, useState } from "react";

export interface AssumptionEditPopoverProps {
  label: string;
  description?: string;
  /** Formatted current (or pending) value, used as the input's initial text. */
  initialValue: string;
  unit?: string | null;
  /** Range hint — assumptions only; omit for facts. */
  range?: { low: number; high: number; step: number } | null;
  /** Confirmation state — undefined means not confirmable (facts). */
  confirmed?: boolean;
  /** Raw input text on Apply/Enter; parsing/validation is the caller's job. */
  onApply: (raw: string) => void;
  onConfirm?: () => void;
  onUnconfirm?: () => void;
  onClose: () => void;
}

const CLS_FOOT_BTN = [
  "inline-flex items-center justify-center",
  "h-8 px-3.5 rounded-(--radius-field)",
  "text-sm font-medium cursor-pointer",
  "transition-[background,color,border-color] duration-120",
].join(" ");

/**
 * Centered overlay edit dialog for a single assumption or fact value.
 * Closes on Escape, backdrop click, or Cancel without side effects; Enter
 * or Apply commits the raw input text via `onApply` and then closes.
 */
export function AssumptionEditPopover({
  label,
  description,
  initialValue,
  unit,
  range,
  confirmed,
  onApply,
  onConfirm,
  onUnconfirm,
  onClose,
}: AssumptionEditPopoverProps) {
  const [text, setText] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  function apply() {
    onApply(text);
    onClose();
  }

  function handleConfirm() {
    onConfirm?.();
    onClose();
  }

  function handleUnconfirm() {
    onUnconfirm?.();
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/30 border-0 cursor-default"
        type="button"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="relative w-[360px] flex flex-col overflow-hidden rounded-(--radius-container) bg-(--bg-white) border border-(--border-subtle) shadow-(--shadow-lg)"
      >
        <header className="px-4 pt-4 pb-2">
          <h2 className="font-(family-name:--font-display) text-sm font-bold tracking-[-0.015em] text-(--fg-primary) m-0">
            {label}
          </h2>
          {description && (
            <p className="text-[12.5px] text-(--fg-muted) mt-1 mb-0">
              {description}
            </p>
          )}
        </header>

        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 rounded-(--radius-field) border border-(--border-subtle) px-2.5 py-1.5">
            <input
              ref={inputRef}
              type="text"
              aria-label={label}
              className="flex-1 min-w-0 border-0 bg-transparent text-sm text-(--fg-primary) outline-none"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") apply();
              }}
            />
            {unit && (
              <span className="text-[12.5px] text-(--fg-muted)">{unit}</span>
            )}
          </div>
          {range && (
            <p className="mt-1.5 mb-0 text-[12px] text-(--fg-muted)">
              Range: {range.low}–{range.high}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-between gap-2 px-4 py-3 border-t border-(--border-subtle)">
          <div>
            {confirmed === false && (
              <button
                type="button"
                className={`${CLS_FOOT_BTN} bg-transparent text-(--fg-secondary) hover:bg-(--bg-hover)`}
                onClick={handleConfirm}
              >
                Confirm value
              </button>
            )}
            {confirmed === true && (
              <button
                type="button"
                className={`${CLS_FOOT_BTN} bg-transparent text-(--fg-secondary) hover:bg-(--bg-hover)`}
                onClick={handleUnconfirm}
              >
                Unconfirm
              </button>
            )}
          </div>
          <div className="inline-flex gap-2">
            <button
              type="button"
              className={`${CLS_FOOT_BTN} text-(--fg-secondary) bg-(--bg-canvas) hover:bg-(--bg-focused) hover:text-(--fg-primary)`}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`${CLS_FOOT_BTN} bg-(--brand-navy) text-(--fg-inverse) hover:bg-(--steel-110)`}
              onClick={apply}
            >
              Apply
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
